import React, { useState } from "react";
import { connect } from "react-redux";
import classnames from "classnames";
import { Button, TextInput, Dropdown, Form } from "carbon-components-react";
import { Renew16, SubtractAlt16, AddAlt16 } from "@carbon/icons-react";
import { sortBy } from "lodash";
import Big from "big.js";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import {
  AppShape,
  dynamicFicoXLtvKey,
  LoanApplicationRate,
  PriceExceptionReason,
  Rate,
  RateAdjusters,
  rateAdjusterToLabel,
  rateAdjusterToRank,
  UpdateLockedRateRequest,
  UpdatePriceException,
} from "@eave/shared/types/api";
import { EMDASH } from "underwriter_dashboard/hurdle_editors/AssetSummary/shared";
import LabeledObject from "web_modules/shared/LabeledObject";
import LabeledStructuredList from "web_modules/shared/LabeledStructuredList";
import { decimalToThreePlaces } from "web_modules/capital_markets_dashboard/shared/utils";
import UnlockBorrowerRate from "underwriter_dashboard/hurdle_editors/BorrowerRateLock/UnlockBorrowerRateModal";
import { formatDateString } from "@eave/shared/utils/date-formatter";
import { rateIsLocked } from "@eave/shared/utils/rates";
import { loadApp } from "underwriter_dashboard/actions";
import APIClient from "@eave/borrower/web_modules/APIClient";
import { ColHeader } from "web_modules/shared/LabeledStructuredList/LabeledStructuredList";
import Money from "web_modules/Money";
import { formatPercent } from "@eave/shared/utils";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");
const borrowerRateLockStyles = require("./styles.module.scss");

const renderBorrowerRateLockSummary = (
  applicationRate: LoanApplicationRate,
  unlockRateButton: Button,
  loanAmount: string,
) => {
  const borrowerRateItems = [
    {
      label: "Rate",
      value:
        applicationRate !== null
          ? `${decimalToThreePlaces(applicationRate.rate_basis.daily_base_rate)}%`
          : EMDASH,
    },
    {
      label: "Loan Type",
      value: applicationRate !== null ? `${applicationRate.rate_basis.loan_type}` : EMDASH,
    },
    {
      label: "Loan Program",
      value: applicationRate !== null ? `${applicationRate.rate_basis.loan_program}` : EMDASH,
    },
    {
      label: "FICO",
      value: applicationRate !== null ? `${applicationRate.rate_basis.fico}` : EMDASH,
    },
    {
      label: "LTV",
      value:
        applicationRate !== null
          ? formatPercent(Number(applicationRate.rate_basis.ltv) * 100, 2)
          : EMDASH,
    },
    {
      label: "Margin",
      value:
        applicationRate !== null && applicationRate?.rate_basis.margin !== "0"
          ? `${applicationRate.rate_basis.margin}%`
          : EMDASH,
    },
    {
      label: "Caps",
      value:
        applicationRate !== null &&
        applicationRate?.rate_basis?.caps &&
        applicationRate.rate_basis.caps !== ""
          ? applicationRate.rate_basis.caps
          : EMDASH,
    },
  ];

  if (
    applicationRate?.rate_basis?.price_delta &&
    Number(applicationRate.rate_basis.price_delta) !== 0
  ) {
    borrowerRateItems.push(
      {
        label:
          Number(applicationRate.rate_basis.price_delta) < 0 ? "Discount Points" : "Lender Credit",
        value: `${applicationRate.rate_basis.price_delta}%`,
      },
      {
        label:
          Number(applicationRate.rate_basis.price_delta) < 0
            ? "Discount Points Amount"
            : "Lender Credit Amount",
        value: (
          <Money
            value={Big(loanAmount)
              .mul(Big(applicationRate.rate_basis.price_delta).div(Big(100)))
              .abs()
              .round(2)
              .toString()}
          />
        ),
      },
    );
  }

  borrowerRateItems.push(
    {
      label: "Effective Date",
      value:
        applicationRate !== null
          ? formatDateString(
              rateIsLocked(applicationRate)
                ? applicationRate.locked_at
                : applicationRate.created_at,
            )
          : EMDASH,
    },
    {
      label: "Expiration Date",
      value: applicationRate !== null ? formatDateString(applicationRate.expires_at) : EMDASH,
    },
  );

  return (
    <LabeledStructuredList
      title="Borrower Rate"
      colHeaders={[
        { displayName: "", dataKey: "label" },
        { displayName: "", dataKey: "value", align: "right" },
      ]}
      rowData={borrowerRateItems}
      action={unlockRateButton}
    />
  );
};

const renderDailyBaseRateSummary = (decisionRate: Rate) => {
  return (
    <LabeledObject
      title="Daily Base Rate"
      listData={[
        {
          label: `Base Rate as of ${formatDateString(decisionRate.daily_base_rate_date)}`,
          value: decimalToThreePlaces(decisionRate.daily_base_rate),
          valueAlign: "right",
        },
      ]}
    />
  );
};

const getRateAdjusterHeadings = (recalculating: boolean): ColHeader[] => {
  if (recalculating) {
    return [
      { displayName: "", dataKey: "rateAdjusterName" },
      { displayName: "Current", dataKey: "currentRate" },
      { displayName: "New", dataKey: "newRate" },
    ];
  }
  return [
    { displayName: "", dataKey: "rateAdjusterName" },
    { displayName: "", dataKey: "currentRate", align: "right" },
  ];
};

const renderRateAdjustments = (rateAdjusters, newRateAdjusters, recalculateButton: Button) => {
  const rateAdjusterItems = sortBy(Object.keys(rateAdjusters), (r: RateAdjusters) =>
    rateAdjusterToRank(r),
  ).map((k: RateAdjusters) => {
    let rateAdjusterName = rateAdjusterToLabel(k);
    let k2 = k;

    // if fico_x_ltv adjuster key find the corresponding one in the new rate
    // adjusters. It may not have the same name.
    if (newRateAdjusters && k.indexOf("fico_x_ltv") !== -1) {
      const newRateAdjusterKeys = Object.keys(newRateAdjusters);

      for (let i = 0; i < newRateAdjusterKeys.length; i++) {
        const rateAdjusterKey = newRateAdjusterKeys[i];

        if (rateAdjusterKey.indexOf("fico_x_ltv") !== -1) {
          k2 = rateAdjusterKey as RateAdjusters;
          if (k !== k2) {
            rateAdjusterName = `${dynamicFicoXLtvKey(k)} ->  ${dynamicFicoXLtvKey(k2)}`;
          }

          break;
        }
      }
    }

    return {
      rateAdjusterName,
      currentRate: decimalToThreePlaces(rateAdjusters[k]),
      newRate: newRateAdjusters !== null ? decimalToThreePlaces(newRateAdjusters[k2]) : EMDASH,
    };
  });

  return (
    <LabeledStructuredList
      title="Rate Adjustments"
      colHeaders={getRateAdjusterHeadings(newRateAdjusters !== null)}
      rowData={rateAdjusterItems}
      action={recalculateButton}
    />
  );
};

const renderPriceExceptions = (
  priceException: UpdatePriceException,
  setPriceException: Function,
) => {
  return (
    <Form className={borrowerRateLockStyles.priceExceptionForm}>
      <Dropdown
        id="reason"
        titleText="Reason"
        label="Select reason"
        items={Object.keys(PriceExceptionReason).map(e => {
          return {
            id: PriceExceptionReason[e],
            text: PriceExceptionReason[e],
          };
        })}
        itemToString={item => (item ? item.text : EMDASH)}
        onChange={e =>
          setPriceException({ reason: e.selectedItem.text, value: priceException.value })
        }
        invalidText="Must add a price exception reason"
        invalid={priceException.value !== null && priceException.reason === null}
        selectedItem={{ id: priceException.reason, text: priceException.reason }}
      />
      <TextInput
        id="reason"
        labelText="Value"
        value={priceException.value}
        onChange={e => setPriceException({ reason: priceException.reason, value: e.target.value })}
        type="number"
        step=".125"
      />
    </Form>
  );
};

export const getExistingPriceException = (
  app: AppShape,
  rateIsSelected: boolean,
): UpdatePriceException | null => {
  const existingPriceException = rateIsSelected
    ? app.active_rate.price_exception
    : app.price_exception;
  if (existingPriceException == null) {
    return null;
  }
  return {
    value: Number(existingPriceException.value),
    reason: existingPriceException.reason,
  };
};

export const getNewFinalRateValue = (
  app: AppShape,
  recalculateAdjusters: boolean,
  rateIsSelected: boolean,
  newPriceException: UpdatePriceException,
) => {
  const existingPriceException = getExistingPriceException(app, rateIsSelected);
  const existingPriceExceptionValue =
    existingPriceException !== null ? existingPriceException.value : 0;
  const newPriceExceptionValue = newPriceException !== null ? newPriceException.value : 0;

  let rateAdjustersTotal: number;
  if (rateIsSelected) {
    if (recalculateAdjusters) {
      // Get the Floating Rate Adjusters total
      // If the rate is selected the calculated rate does not take into account for price exception applied to the selected rate
      rateAdjustersTotal =
        Number(app.decision.calculated_rate.displayed_loan_rate) -
        Number(app.decision.calculated_rate.daily_base_rate);
    } else {
      // Get the Locked Rate Adjusters total
      rateAdjustersTotal =
        Number(app.active_rate.rate) -
        Number(app.active_rate.rate_basis.daily_base_rate) -
        Number(existingPriceExceptionValue);
    }
  } else {
    // Get the Floating Rate Adjusters total
    rateAdjustersTotal =
      Number(app.decision.calculated_rate.displayed_loan_rate) -
      Number(app.decision.calculated_rate.daily_base_rate) -
      Number(existingPriceExceptionValue);
  }

  const baseRate = rateIsSelected
    ? app.active_rate.rate_basis.daily_base_rate
    : app.decision.calculated_rate.daily_base_rate;

  const newRate = Number(baseRate) + Number(rateAdjustersTotal) + Number(newPriceExceptionValue);

  return Number(newRate).toFixed(3);
};

const validate = (priceException: UpdatePriceException | null) => {
  if (priceException !== null) {
    if (priceException.reason === null || !priceException.reason) {
      alert("Must provide a Reason for the Price Exception");
      throw new Error("Must provide a Reason for the Price Exception");
    }
    if (
      priceException.value === null ||
      Number.isNaN(priceException.value) ||
      !priceException.value
    ) {
      alert("Must provide a Value for the Price Exception");
      throw new Error("Must provide a Value for the Price Exception");
    }
  }
};

function saveNewRateToLoanApplication(
  appId: number,
  recalculateAdjusters: boolean,
  newPriceException: UpdatePriceException | null,
) {
  const payload: UpdateLockedRateRequest = {
    recalculate_adjusters: recalculateAdjusters,
    price_exception: newPriceException,
  };

  return APIClient.patch(`/uw/loan_applications/${appId}/loan_application_rate`, payload);
}

function deletePriceException(appId: number) {
  return APIClient.deleteRequest(`/uw/loan_applications/${appId}/price_exception`);
}

function addPriceException(appId: number, newPriceException: UpdatePriceException) {
  return APIClient.put(`/uw/loan_applications/${appId}/price_exception`, newPriceException);
}

function saveToApp(
  appId: number,
  rateIsSelected: boolean,
  recalculatedAdjusters: boolean,
  newPriceException: UpdatePriceException | null,
) {
  if (rateIsSelected) {
    return saveNewRateToLoanApplication(appId, recalculatedAdjusters, newPriceException);
  }
  if (newPriceException === null) {
    // Price exception has been removed from loan application
    return deletePriceException(appId);
  }
  return addPriceException(appId, newPriceException);
}

interface HurdleEditorProps extends HurdleProps {
  reloadApp: Function;
}

const BorrowerRateLockHurdle = (props: HurdleEditorProps) => {
  const { app, hurdle, onHide, reloadApp } = props;
  const [recalculate, setRecalculate] = useState(false);

  const rateIsSelected = app.active_rate !== null;
  const existingPriceException: UpdatePriceException = getExistingPriceException(
    app,
    rateIsSelected,
  );

  const [priceException, setPriceException] = useState(existingPriceException);

  const canRecalculateRateAdjusters = rateIsLocked(app.active_rate);

  const resetScreen = () => {
    setRecalculate(false);
    setPriceException(existingPriceException);
  };

  const togglePriceException = () => {
    if (priceException) {
      setPriceException(null);
    } else {
      setPriceException({ value: null, reason: null });
    }
  };

  const save = () => {
    validate(priceException);
    saveToApp(app.id, rateIsSelected, recalculate, priceException)
      .catch(err => {
        if (err?.meta?.error_detail) {
          alert(err.meta.error_detail);
        } else {
          alert(
            `there was an error saving the rate, please contact engineering or reload the page and try again`,
          );
        }

        throw new Error(
          `there was an error saving a new rate to loan application Id: ${app.id}. Error: ${err}`,
        );
      })
      .finally(() => {
        reloadApp(app.id);
      });
  };

  const hasChanged = () => {
    return recalculate || existingPriceException !== priceException; // TODO: How does this work with null !== null etc.
  };

  const newRate = getNewFinalRateValue(app, recalculate, rateIsSelected, priceException);

  const unlockRateButton = (
    <UnlockBorrowerRate
      activeRate={app.active_rate}
      loanApplicationId={app.id}
      reloadLoanApp={reloadApp}
    />
  );

  const recalculateButton = (
    <Button
      iconDescription="Recalculate"
      kind="tertiary"
      onClick={() => setRecalculate(true)}
      renderIcon={Renew16}
      disabled={!canRecalculateRateAdjusters}
    >
      Recalculate
    </Button>
  );

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      <div className={borrowerRateLockStyles.rateLockContainer}>
        <div className={borrowerRateLockStyles.listWrapper}>
          {renderDailyBaseRateSummary(app.decision.calculated_rate)}
        </div>
      </div>
      <div className={borrowerRateLockStyles.rateLockContainer}>
        <div className={borrowerRateLockStyles.listWrapper}>
          {renderBorrowerRateLockSummary(
            app.active_rate,
            unlockRateButton,
            app.computed_properties.loan_amount,
          )}
        </div>
      </div>
      <div className={borrowerRateLockStyles.rateLockContainer}>
        <div className={borrowerRateLockStyles.listWrapper}>
          {recalculate
            ? renderRateAdjustments(
                app.decision.rate.rate_adjusters,
                app.decision.calculated_rate.rate_adjusters,
                recalculateButton,
              )
            : renderRateAdjustments(app.decision.rate.rate_adjusters, null, recalculateButton)}
        </div>
      </div>
      <div className={borrowerRateLockStyles.rateLockContainer}>
        <div className={borrowerRateLockStyles.listWrapper}>
          <h3>Rounded rate before price exception</h3>
          <LabeledObject
            listData={[
              {
                label: "Rate",
                value: decimalToThreePlaces(
                  String(
                    Number(app.decision.rate.displayed_loan_rate) -
                      Number(app.decision.rate.price_exception_amount || 0),
                  ),
                ),
                valueAlign: "right",
              },
            ]}
          />
        </div>
      </div>
      <div className={borrowerRateLockStyles.rateLockContainer}>
        <div className={borrowerRateLockStyles.listWrapper}>
          <div className={borrowerRateLockStyles.listHeading}>
            <h3>Price Exception</h3>
            <Button
              iconDescription={priceException ? "Delete Exception" : "Add Exception"}
              kind="tertiary"
              onClick={togglePriceException}
              renderIcon={priceException ? SubtractAlt16 : AddAlt16}
            >
              {priceException ? "Delete Exception" : "Add Exception"}
            </Button>
          </div>
          {priceException && renderPriceExceptions(priceException, setPriceException)}
        </div>
      </div>
      <footer className={classnames(styles.footer, borrowerRateLockStyles.fullWidth)}>
        <span className={borrowerRateLockStyles.finalRateLabel}>Final Rate:</span>
        <span className={borrowerRateLockStyles.finalRateValue}>
          {!hasChanged()
            ? `${decimalToThreePlaces(app.decision.effective_rate)}%`
            : `${newRate}% (was ${decimalToThreePlaces(app.decision.effective_rate)}%)`}
        </span>
        {hasChanged() && (
          <div className={styles.buttons}>
            <Button kind="secondary" tabIndex={0} onClick={resetScreen}>
              Cancel
            </Button>
            <Button kind="primary" type="submit" tabIndex={0} onClick={save}>
              Save
            </Button>
          </div>
        )}
      </footer>
    </HurdleView>
  );
};

const mapDispatchToProps = dispatch => ({
  reloadApp: appID => {
    loadApp(dispatch, appID);
  },
});

export default connect<null, any>(null, mapDispatchToProps)(BorrowerRateLockHurdle);
